<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

/**
 * The `@` autocomplete for a group conversation on a channel that has members to mention. Sibling of
 * TagAgents, which offers Chatwoot agents inside private notes; this one offers the people on the other
 * side of the channel, so it inserts plain text instead of an agent mention node.
 */
const props = defineProps({
  searchKey: { type: String, default: '' },
  participants: { type: Array, default: () => [] },
});

const emit = defineEmits(['selectParticipant']);

const listRef = ref(null);
const selectedIndex = ref(0);

// Matches the number as well as the name, since a member Chatwoot has no contact for is listed by number.
const items = computed(() => {
  const search = props.searchKey?.trim().toLowerCase() || '';
  if (!search) return props.participants;

  return props.participants.filter(
    ({ name, number }) =>
      name.toLowerCase().includes(search) || number.includes(search)
  );
});

const adjustScroll = () => {
  nextTick(() => {
    listRef.value
      ?.querySelector(`#participant-item-${selectedIndex.value}`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  });
};

const onSelect = () => {
  const participant = items.value[selectedIndex.value];
  if (participant) emit('selectParticipant', participant);
};

useKeyboardNavigableList({ items, onSelect, adjustScroll, selectedIndex });

watch(items, list => {
  if (list.length < selectedIndex.value + 1) selectedIndex.value = 0;
});
</script>

<template>
  <ul
    v-if="items.length"
    ref="listRef"
    class="vertical dropdown menu mention--box bg-n-solid-1 p-1 rounded-xl text-sm overflow-auto absolute w-full z-20 shadow-md left-0 leading-[1.2] bottom-full max-h-[12.5rem] border border-solid border-n-strong"
    role="listbox"
  >
    <li v-for="(item, index) in items" :key="item.number">
      <button
        :id="`participant-item-${index}`"
        type="button"
        class="flex items-center justify-between w-full gap-2 px-2 py-1.5 rounded-lg text-left"
        :class="{ 'bg-n-alpha-2': index === selectedIndex }"
        @click="
          selectedIndex = index;
          onSelect();
        "
        @mouseover="selectedIndex = index"
      >
        <span class="truncate text-n-slate-12">{{ item.name }}</span>
        <span class="shrink-0 text-xs text-n-slate-11">+{{ item.number }}</span>
      </button>
    </li>
  </ul>
</template>
